import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { contentPages } from '../../seo';

// ---- Prose primitives (consistent typography for content pages) ------------

export function H2({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <h2 id={id} className="mt-14 mb-4 scroll-mt-24 text-2xl font-bold text-text-primary md:text-3xl">
      {children}
    </h2>
  );
}

export function H3({ children }: { children: ReactNode }) {
  return <h3 className="mt-8 mb-3 text-lg font-semibold text-text-primary md:text-xl">{children}</h3>;
}

export function P({ children }: { children: ReactNode }) {
  return <p className="mb-5 leading-relaxed text-text-secondary">{children}</p>;
}

export function UL({ children }: { children: ReactNode }) {
  return <ul className="mb-6 space-y-3">{children}</ul>;
}

export function LI({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-3 leading-relaxed text-text-secondary">
      <span className="mt-2 flex h-1.5 w-1.5 shrink-0 rounded-full bg-red-accent" />
      <span>{children}</span>
    </li>
  );
}

export function Callout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="my-8 rounded-xl border border-red-accent/20 bg-red-subtle p-6">
      <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-red-accent">{title}</p>
      <div className="text-sm leading-relaxed text-text-secondary [&_p:last-child]:mb-0">{children}</div>
    </div>
  );
}

// ---- Page layout -----------------------------------------------------------

interface ArticleLayoutProps {
  breadcrumb: string;
  eyebrow: string;
  title: ReactNode;
  lead: string;
  currentPath: string;
  children: ReactNode;
}

export function ArticleLayout({
  breadcrumb,
  eyebrow,
  title,
  lead,
  currentPath,
  children,
}: ArticleLayoutProps) {
  const related = contentPages.filter((c) => c.path !== currentPath);

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden px-6 pb-8 pt-16 md:pt-24">
        <div className="pointer-events-none absolute -top-32 right-1/4 h-[500px] w-[500px] rounded-full bg-red-accent/5 blur-3xl" />
        <div className="relative mx-auto max-w-3xl">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-1.5 text-xs text-text-secondary">
            <Link to="/" className="transition-colors hover:text-text-primary">
              Home
            </Link>
            <ChevronRight size={14} />
            <span className="text-text-primary">{breadcrumb}</span>
          </nav>

          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-red-accent">{eyebrow}</p>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight md:text-5xl">{title}</h1>
          <p className="mt-6 text-lg leading-relaxed text-text-secondary">{lead}</p>
        </div>
      </section>

      {/* Body */}
      <article className="px-6 pb-16">
        <div className="mx-auto max-w-3xl">{children}</div>
      </article>

      {/* Related reading */}
      <section className="border-t border-border bg-surface px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-6 text-xl font-bold text-text-primary">Related reading</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {related.map((r) => (
              <Link
                key={r.path}
                to={r.path}
                className="group rounded-xl border border-border bg-background p-5 transition-colors hover:border-red-accent/40"
              >
                <p className="mb-1 font-semibold text-text-primary group-hover:text-red-accent">{r.label}</p>
                <p className="text-sm leading-relaxed text-text-secondary">{r.blurb}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-red-subtle to-surface px-6 py-20 md:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-text-primary md:text-4xl">
            See how Redbax stops AI data leaks.
          </h2>
          <p className="mt-4 text-lg text-text-secondary">
            AI endpoint DLP for ChatGPT, Copilot, Claude and 40+ AI tools. Be first to know when we launch.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-red-accent px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-red-hover"
          >
            Register Interest
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
